import { Grid } from '@mui/material';
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import React, { useImperativeHandle } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { StepperFromProps } from 'sharedComponents/dynamicStepperForm/dynamicStepperFormInterface';
import { CommonInput, SelectInput } from 'sharedComponents/inputGroup';
import { RootState } from 'store/store';
import PreApprovedHeader from './header/PreApprovedHeader';
import { errorMessage } from 'constants/CommonConstants';
import { formRequiredRules } from 'helper';
import { EMPLOYMENT_STATUS_OPTION } from 'pages/leadGen/LeadGenConstant';
import { AutoCompleteAddress } from 'sharedComponents/inputGroup/AutoCompleteAddress';
import { INCOME_FREQUENCY_OPTION, PROVINCE } from '../DigitalRetailingConstant';
import { employeeHistory } from 'store/CreditScoreState';
import { EmploymentDetails } from 'pages/creditScore/CreditScoreInterface';

const Step10: React.ForwardRefRenderFunction<unknown, StepperFromProps> = ({ onNext, onBack }, ref) => {
  // Hooks
  const { t } = useTranslation();

  const { control, trigger, setValue, watch, getValues } = useFormContext();

  const dispatch: ThunkDispatch<RootState, AnyAction, AnyAction> = useDispatch();

  const {
    leadCreationFormData: { data: { Lead: { _id } = { _id: '' } } = { Lead: { _id: '' } } },
  } = useSelector((state: RootState) => state.leadCreationStore);

  const hasCalledNextStep = React.useRef(false);

  const employmentStatusWatcher = watch('employmentStatus');
  const otherIncomeTypeWatcher = watch('otherIncomeType');
  const incomeFrequencyWatcher = watch('incomeFrequency');
  const pre_approved_emp_address_provinceWatcher = watch('pre_approved_emp_address_province');

  // React Hook Form Trigger
  const nextStep = () => {
    if (hasCalledNextStep.current) return;
    hasCalledNextStep.current = true;

    // onNext?.();

    const {
      employmentStatus,
      employerName,
      occupation,
      pre_approved_emp_address,
      pre_approved_emp_address_city,
      pre_approved_emp_address_province,
      pre_approved_emp_address_postalCode,
      totalYearsEmployment,
      totalMonthsEmployment,
      grossEmploymentIncome,
      incomeFrequency,
      otherIncomeType,
    } = getValues();

    const updatedData: EmploymentDetails = {
      // address: {
      //   yearsAtResidence: Number(totalYearsEmployment),
      //   monthsAtResidence: Number(totalMonthsEmployment),
      // },
      employeeHistory: {
        yearsAtJob: Number(totalYearsEmployment),
        monthsAtJob: Number(totalMonthsEmployment),
        employer: employerName,
        income: grossEmploymentIncome,
        occupation: occupation,
      },

      leadId: _id,
    };

    dispatch(employeeHistory(updatedData)).then((res) => {
      if (Object.keys(res?.payload).length && res?.payload?.message) {
        // clearLocalStorage();
      }
    });
  };

  const backStep = () => {
    onBack?.();
  };

  useImperativeHandle(ref, () => ({
    nextStep,
    backStep,
  }));

  return (
    <>
      <PreApprovedHeader currentStep={1} />

      <div className='dr-widget-steps pre-approved-steps'>
        <div className='dr-widget-steps__content'>
          <h2 className='dr-widget-steps__title'>{t('digitalRetailing.getPreApprovedNow')}</h2>
          <p className='dr-widget-steps__step-note text-secondary'>{t('digitalRetailing.completeApplications')}</p>
          <div className='dr-widget-steps__form-wrapper'>
            <h3 className='form-section-title'>{t('digitalRetailing.currentEmploymentInformation')}</h3>
            <Grid container spacing={2} marginBottom={3}>
              <Grid item md={4} sm={6} xs={12} marginBottom={3}>
                <SelectInput
                  name='employmentStatus'
                  control={control}
                  label={t('digitalRetailing.employmentStatus')}
                  rules={formRequiredRules(errorMessage(t).required)}
                  options={EMPLOYMENT_STATUS_OPTION(t)}
                  triggerValidationForField={trigger}
                  defaultValue={employmentStatusWatcher}
                />
              </Grid>
              <Grid item md={4} sm={6} xs={12} marginBottom={3}>
                <CommonInput
                  name='employerName'
                  control={control}
                  label={t('digitalRetailing.employerName')}
                  placeholder={t('')}
                  triggerValidationForField={trigger}
                  rules={formRequiredRules(errorMessage(t).required)}
                />
              </Grid>
              <Grid item md={4} sm={6} xs={12} marginBottom={3}>
                <CommonInput
                  name='occupation'
                  control={control}
                  label={t('digitalRetailing.occupation')}
                  placeholder={t('')}
                  triggerValidationForField={trigger}
                  rules={formRequiredRules(errorMessage(t).required)}
                />
              </Grid>
              <Grid item md={4} sm={6} xs={12} marginBottom={3}>
                <AutoCompleteAddress
                  name='pre_approved_emp_address'
                  control={control}
                  setValue={setValue}
                  label={t('stepperForm.address')}
                  placeholder={t('stepperForm.enterAddress')}
                  rules={formRequiredRules(errorMessage(t).required)}
                  triggerValidationForField={trigger}
                />
              </Grid>
              <Grid item md={4} sm={6} xs={12} marginBottom={3}>
                <CommonInput
                  name='pre_approved_emp_address_city'
                  control={control}
                  label={t('digitalRetailing.employmentCity')}
                  triggerValidationForField={trigger}
                  rules={formRequiredRules(errorMessage(t).required)}
                />
              </Grid>
              <Grid item md={4} sm={6} xs={12} marginBottom={3}>
                <SelectInput
                  name='pre_approved_emp_address_province'
                  control={control}
                  label={t('stepperForm.province')}
                  rules={formRequiredRules(errorMessage(t).provinceRequired)}
                  options={PROVINCE}
                  defaultValue={pre_approved_emp_address_provinceWatcher}
                  triggerValidationForField={trigger}
                />
              </Grid>
              <Grid item md={4} sm={6} xs={12} marginBottom={3}>
                <CommonInput
                  name='pre_approved_emp_address_postalCode'
                  control={control}
                  label={t('stepperForm.postalCode')}
                  placeholder={t('')}
                  triggerValidationForField={trigger}
                  rules={formRequiredRules(errorMessage(t).required)}
                />
              </Grid>
              <Grid item md={4} sm={6} xs={12} marginBottom={3}>
                <CommonInput
                  name='totalYearsEmployment'
                  control={control}
                  label={t('digitalRetailing.totalYearsAtEmployment')}
                  placeholder={t('')}
                  triggerValidationForField={trigger}
                  rules={formRequiredRules(errorMessage(t).required)}
                  type='number'
                />
              </Grid>
              <Grid item md={4} sm={6} xs={12} marginBottom={3}>
                <CommonInput
                  name='totalMonthsEmployment*'
                  control={control}
                  label={t('digitalRetailing.totalMonthsAtEmployment')}
                  placeholder={t('')}
                  triggerValidationForField={trigger}
                  rules={formRequiredRules(errorMessage(t).required)}
                  type='number'
                />
              </Grid>
            </Grid>
            <h3 className='form-section-title'>{t('digitalRetailing.currentIncomeInformation')}</h3>
            <Grid container spacing={2} marginBottom={3}>
              <Grid item md={4} sm={6} xs={12} marginBottom={3}>
                <CommonInput
                  name='grossEmploymentIncome'
                  control={control}
                  label={t('digitalRetailing.grossEmploymentIncome')}
                  triggerValidationForField={trigger}
                  rules={formRequiredRules(errorMessage(t).required)}
                  type='number'
                />
              </Grid>
              <Grid item md={4} sm={6} xs={12} marginBottom={3}>
                <SelectInput
                  name='incomeFrequency'
                  control={control}
                  label={t('digitalRetailing.incomeFrequency')}
                  rules={formRequiredRules(errorMessage(t).required)}
                  options={INCOME_FREQUENCY_OPTION(t)}
                  triggerValidationForField={trigger}
                  defaultValue={incomeFrequencyWatcher}
                />
              </Grid>
              <Grid item md={4} sm={6} xs={12}>
                <SelectInput
                  name='otherIncomeType'
                  control={control}
                  label={t('digitalRetailing.otherIncomeType')}
                  rules={formRequiredRules(errorMessage(t).required)}
                  options={[{ label: 'None', value: 'none' }]}
                  triggerValidationForField={trigger}
                  defaultValue={otherIncomeTypeWatcher}
                />
              </Grid>
            </Grid>
          </div>
        </div>
      </div>
    </>
  );
};

const StepperForm10 = React.forwardRef(Step10);

export default StepperForm10;
